/**
 * Backoffice CRUD Types
 * Types matching the exact API response from credit-engine-business BackofficeCrudManager
 * All fields use snake_case to match DynamoDB/API conventions
 */

// ─── Endpoint 1: GET /backoffice/credits?partnerId= ────────────────────────

export interface BackofficeCreditsListItem {
  credit_id: string;
  directory_id: string;
  partner_id: string;
  phone_number: string;
  amount_mxn: number;
  term_weeks: number;
  annual_rate: number;
  weekly_payment_mxn: number;
  status: string;
  credit_level: string;
  disbursed_at: string;
  created_at: string;
  full_name?: string | null;
  weeks_paid?: number;
  weeks_total?: number;
  next_due_date?: string | null;
  /** Cupones vencidos (status != PAID && due_date < hoy). Item 026. */
  overdue_coupons_count?: number;
  /** Monto en mora real: sum(total_mxn - paid_amount_mxn) de cupones vencidos. */
  overdue_amount_mxn?: number;
  /** due_date del cupon vencido mas antiguo (ISO YYYY-MM-DD). null si esta al dia. */
  first_overdue_due_date?: string | null;
  /** total_mxn del cupon vivo (puede diferir de weekly_payment_mxn por redondeos). Item 030. */
  next_due_total_mxn?: number;
  /** Saldo pendiente del cupon vivo: max(0, total_mxn - paid_amount_mxn). Item 030. */
  next_due_pending_amount_mxn?: number;
  /** true si el cupon vivo tiene paid_amount_mxn > 0 && < total_mxn. Item 030. */
  next_due_partial_paid?: boolean;
}

// ─── Endpoint 2: GET /backoffice/credits/{creditId}?directoryId= ────────────

/** Full credit entity returned from DynamoDB (the `credit` field in BackofficeCreditDetail) */
export interface CreditEntity {
  pk: string;
  sk: string;
  credit_id: string;
  application_id: string;
  directory_id: string;
  partner_id: string;
  status: string;
  amount_mxn: number;
  term_weeks: number;
  annual_rate: number;
  weekly_payment_mxn: number;
  total_interest_mxn: number;
  total_iva_mxn: number;
  total_payable_mxn: number;
  fiado_rate_points: number;
  partner_rate_points: number;
  fiado_interest_mxn: number;
  partner_interest_mxn: number;
  credit_level: string;
  disbursed_at: string;
  first_payment_date: string;
  last_payment_date: string;
  created_at: string;
  updated_at: string;
  // Opening fee snapshot (presente solo en creditos originados con comision)
  opening_fee_pct?: number;
  opening_fee_mxn?: number;
  opening_fee_iva_mxn?: number;
  financed_amount_mxn?: number;
}

/** Payment schedule item (coupon) from CreditPaymentSchedule table */
export interface PaymentScheduleCoupon {
  pk: string;
  sk: string;
  credit_id: string;
  coupon_number: number;
  due_date: string;
  principal_mxn: number;
  interest_mxn: number;
  iva_mxn: number;
  total_mxn: number;
  fiado_interest_mxn: number;
  partner_interest_mxn: number;
  remaining_balance_mxn: number;
  status: string; // PENDING | PAID | PARTIAL | OVERDUE | OVERDUE_PARTIAL | WRITTEN_OFF
  paid_amount_mxn: number;
  anticipated_reduction_mxn?: number;
  created_at: string;
  updated_at: string;
}

/** User info enriched from fiado-directory-lambda + fiado-identity-lambda */
export interface BackofficeUserInfo {
  directory_id: string;
  phone_number: string;
  people_id: string;
  full_name: string;
}

/** Response for credit detail endpoint */
export interface BackofficeCreditDetail {
  credit: CreditEntity;
  user: BackofficeUserInfo;
  schedule: PaymentScheduleCoupon[];
}

// ─── Endpoint 3: GET /backoffice/applications?partnerId= ────────────────────

export interface BackofficeApplicationsListItem {
  application_id: string;
  directory_id: string;
  partner_id: string;
  phone_number: string;
  full_name: string;
  amount_mxn: number;
  term_weeks: number;
  annual_rate: number;
  weekly_payment_mxn: number;
  credit_level: string;
  status: string;
  signed_at: string;
  created_at: string;
}

// ─── Endpoint 4: GET /backoffice/applications/{applicationId}?directoryId= ──

/** Full application entity from DynamoDB */
export interface ApplicationEntity {
  pk: string;
  sk: string;
  application_id: string;
  directory_id: string;
  partner_id: string;
  offer_id: string;
  status: string; // CREATED | PENDING_SIGNATURE | SIGNED | DISBURSED | EXPIRED | CANCELLED
  amount_mxn: number;
  term_weeks: number;
  annual_rate: number;
  weekly_payment_mxn: number;
  credit_level?: string; // BRONZE | SILVER | GOLD | PLATINUM
  contract_view_url: string;
  expires_at: string;
  signed_at?: string;
  created_at: string;
  updated_at: string;
  // Opening fee snapshot (presente solo en applications con comision)
  opening_fee_pct?: number;
  opening_fee_mxn?: number;
  opening_fee_iva_mxn?: number;
  financed_amount_mxn?: number;
}

/** Response for application detail endpoint */
export interface BackofficeApplicationDetail {
  application: ApplicationEntity;
  user: BackofficeUserInfo;
}

// ─── Endpoint 5: GET /backoffice/offers?partnerId= ──────────────────────────

/** Rate option available in an offer */
export interface AvailableRate {
  term_weeks: number;
  annual_rate: number;
}

export interface BackofficeOffersListItem {
  offer_id: string;
  directory_id: string;
  partner_id: string;
  phone_number: string;
  full_name: string;
  credit_level: string;
  max_amount_mxn: number;
  min_amount_mxn: number;
  available_rates: AvailableRate[];
  status: string; // VIEWING | LOCKED | ACCEPTED | ABANDONED | CONVERTED | EXPIRED
  offer_expires_at: string;
  created_at: string;
}

// ─── Endpoint 6: GET /backoffice/offers/{offerId}?directoryId= ──────────────

/** Offer option (payment plan) generated by InterestCalculationService */
export interface OfferOption {
  term_weeks: number;
  annual_rate: number;
  weekly_payment_mxn: number;
  total_to_pay_mxn: number;
  total_interest_mxn: number;
  total_iva_mxn: number;
  savings_vs_longest: number;
  is_recommended: boolean;
  recommendation_reason?: string;
  // Opening fee fields (presentes cuando el partner tiene comision configurada)
  requested_amount_mxn?: number;
  financed_amount_mxn?: number;
  opening_fee_pct?: number;
  opening_fee_mxn?: number;
  opening_fee_iva_mxn?: number;
  cat?: number;
}

/** Full offer entity from DynamoDB */
export interface OfferEntity {
  pk: string;
  sk: string;
  offer_id: string;
  directory_id: string;
  partner_id: string;
  status: string;
  credit_level: string;
  max_amount_mxn: number;
  min_amount_mxn: number;
  selected_amount_mxn: number;
  available_rates: AvailableRate[];
  options: OfferOption[];
  offer_expires_at: string;
  created_at: string;
  updated_at: string;
  // Fields populated when client selects a plan (LOCKED/ACCEPTED/CONVERTED)
  profile_id?: string;
  selected_term_weeks?: number;
  selected_rate?: number;
  weekly_payment_mxn?: number;
  total_to_pay_mxn?: number;
  total_interest_mxn?: number;
  accepted_at?: string;
  abandoned_at?: string;
}

/** Response for offer detail endpoint */
export interface BackofficeOfferDetail {
  offer: OfferEntity;
  user: BackofficeUserInfo;
}

// ─── Endpoint 7: GET /backoffice/profiles/{directoryId}?partnerId= ──────────

/**
 * Full financial profile from ClientFinancialProfile DynamoDB table.
 * 59+ fields organized by category.
 * This is the raw entity returned by the API (no transformation in manager).
 */
export interface FinancialProfileEntity {
  // Keys
  pk: string;
  sk: string;
  directory_id: string;
  partner_id: string;
  people_id?: string | null; // V2 — pending backend support
  phone_number?: string | null; // V2 — pending backend support
  full_name?: string | null; // V2 — pending backend support (type-kit 3.57.0+)
  calculation_date: string;

  // ── Income (from P1: Detection) ──────────────────────────────────────
  total_income_sources: number;
  primary_sources: number;
  secondary_sources: number;
  has_regular_income: string; // "YES" | "NO"
  total_income_90d_usd: number;
  monthly_income_usd: number;
  primary_income_usd: number;
  secondary_income_usd: number;
  dominant_frequency: string; // "WEEKLY" | "BIWEEKLY" | "MONTHLY" | "IRREGULAR"
  days_since_last_deposit: number;
  primary_amount_variation_pct: number;

  // ── Expenses ─────────────────────────────────────────────────────────
  total_expenses_90d_usd: number;
  monthly_expenses_usd: number;
  expense_income_ratio: number;

  // ── Wallet Loads (recargas a wallet de pesos) ────────────────────────
  sends_to_mexico_wallet: string; // "YES" | "NO"
  total_wallet_loads_90d_usd: number;
  monthly_wallet_loads_usd: number;
  wallet_load_count_90d: number;
  avg_wallet_load_usd: number;
  wallet_load_income_ratio: number;
  days_since_last_wallet_load: number;
  wallet_load_frequency: string; // "WEEKLY" | "BIWEEKLY" | "MONTHLY" | "IRREGULAR"

  // ── Balances ─────────────────────────────────────────────────────────
  current_balance_usd: number;
  average_balance_90d_usd: number;
  balance_income_ratio: number;

  // ── Seniority ────────────────────────────────────────────────────────
  registration_date: string;
  months_in_platform: number;

  // ── Exchange Rate ────────────────────────────────────────────────────
  exchange_rate_usd_mxn: number;
  monthly_income_mxn: number;

  // ── Scores (0-100) ───────────────────────────────────────────────────
  score_income_stability: number;
  score_payment_capacity: number;
  score_mexico_transfers: number;
  score_seniority: number;
  score_income_level: number;
  score_total: number;

  // ── Credit Level & Eligibility ───────────────────────────────────────
  credit_level: string | null; // "BRONZE" | "SILVER" | "GOLD" | "PLATINUM" | null
  max_credit_amount_mxn: number;
  has_active_credit: string; // "YES" | "NO"
  is_eligible: string; // "YES" | "NO"
  ineligibility_reason: string | null;

  // ── Gamification ─────────────────────────────────────────────────────
  score_to_next_level: number;
  next_level: string | null; // "SILVER" | "GOLD" | "PLATINUM" | null
  next_level_max_credit_mxn: number | null;
  progress_pct: number;
  recommendation_1: string | null;
  recommendation_1_points: number | null;
  recommendation_2: string | null;
  recommendation_2_points: number | null;
  last_level_change_date: string | null;
  level_change_direction: string; // "UP" | "DOWN" | "NONE"

  // ── Audit ────────────────────────────────────────────────────────────
  created_at: string;
  updated_at: string;
}

// ─── Status Enums (for UI filters and badges) ───────────────────────────────

export const CREDIT_STATUSES = ['ACTIVE', 'DELINQUENT', 'DEFAULT', 'PAID_OFF', 'CANCELLED', 'RESTRUCTURED', 'SETTLED'] as const;
export type CreditStatusType = typeof CREDIT_STATUSES[number];

export const APPLICATION_STATUSES = ['CREATED', 'PENDING_SIGNATURE', 'SIGNED', 'DISBURSED', 'EXPIRED', 'CANCELLED'] as const;
export type ApplicationStatusType = typeof APPLICATION_STATUSES[number];

export const OFFER_STATUSES = ['VIEWING', 'LOCKED', 'ACCEPTED', 'ABANDONED', 'EXPIRED', 'CONVERTED'] as const;
export type OfferStatusType = typeof OFFER_STATUSES[number];

export const CREDIT_LEVELS = ['BRONZE', 'SILVER', 'GOLD', 'PLATINUM'] as const;
export type CreditLevelType = typeof CREDIT_LEVELS[number];

export const COUPON_STATUSES = ['PENDING', 'PAID', 'PARTIAL', 'OVERDUE', 'OVERDUE_PARTIAL', 'WRITTEN_OFF'] as const;
export type CouponStatusType = typeof COUPON_STATUSES[number];
